import { AdoptionPanel } from './AdoptionPanel';
import React, { useState, useEffect } from 'react';
import {
  Activity,
  ShieldCheck,
  Zap,
  Database,
  Cpu,
  Layers,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Download,
  Flame,
  Info,
  Server,
  Radio,
  HardDrive,
  Globe2
} from 'lucide-react';
import { PlatformMetrics, TenantWorkspace } from '../types';
import { api } from '../services/api';

interface PlatformStatsProps {
  activeTenant?: TenantWorkspace | null;
}

/**
 * Renders a measured number, or an explicit em dash when the platform has no
 * record for it. Nothing on this page substitutes an illustrative value for a
 * missing one: a dash that can be explained is more useful than a figure that
 * cannot be traced back to a row.
 */
const Measured: React.FC<{ value: number | null | undefined; suffix?: string; absent?: string }> = ({
  value,
  suffix,
  absent = 'Not yet recorded'
}) => {
  if (value === null || value === undefined) {
    return (
      <span
        title={absent}
        className="cursor-help font-normal text-slate-400 dark:text-slate-500"
      >
        &mdash;
      </span>
    );
  }
  return (
    <>
      {value.toLocaleString()}
      {suffix ? <span className="text-sm font-normal text-slate-400"> {suffix}</span> : null}
    </>
  );
};

const BindingBadge: React.FC<{ state: string }> = ({ state }) => {
  const bound = state === 'BOUND';
  return (
    <span
      className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-bold ${
        bound
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
          : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
      }`}
    >
      {state}
    </span>
  );
};

const MODE_PRESENTATION: Record<
  PlatformMetrics['evidence_basis']['mode'],
  { label: string; tone: 'amber' | 'emerald' | 'slate' }
> = {
  demo: { label: 'DEMO WORKSPACE', tone: 'amber' },
  empty: { label: 'NO DATA RECORDED', tone: 'slate' },
  seed: { label: 'SEEDED DEMO DATA', tone: 'amber' },
  live: { label: 'LIVE TELEMETRY', tone: 'emerald' }
};

export const PlatformStats: React.FC<PlatformStatsProps> = ({ activeTenant }) => {
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  const fetchMetrics = async () => {
    try {
      setIsRefreshing(true);
      const data = await api.getPlatformMetrics();
      setMetrics(data);
      setError(null);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Failed to fetch platform metrics:', err);
      setError('Could not reach the metrics endpoint. Retrying on the next refresh.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30_000); // 30s auto-refresh
    return () => clearInterval(interval);
  }, [activeTenant]);

  const handleExportJson = () => {
    if (!metrics) return;
    const blob = new Blob([JSON.stringify(metrics, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agenticscale-platform-metrics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = () => {
    if (!metrics) return;
    const cell = (value: number | string | null) => (value === null ? '' : String(value));
    const rows = [
      ['Category', 'Metric', 'Value'],
      ['Provenance', 'Data Mode', metrics.evidence_basis.mode],
      ['Provenance', 'First Event At', cell(metrics.evidence_basis.measurement_window?.first_event_at ?? null)],
      ['Provenance', 'Last Event At', cell(metrics.evidence_basis.measurement_window?.last_event_at ?? null)],
      ['Enforcement', 'Total Evaluated Actions', cell(metrics.enforcement.total_evaluated_actions)],
      ['Enforcement', 'Allowed Actions', cell(metrics.enforcement.allowed)],
      ['Enforcement', 'Blocked Actions', cell(metrics.enforcement.blocked)],
      ['Enforcement', 'Held for Review', cell(metrics.enforcement.held_for_review)],
      ['Enforcement', 'Average Latency (ms)', cell(metrics.enforcement.avg_latency_ms)],
      ['Enforcement', 'p95 Latency (ms)', cell(metrics.enforcement.p95_latency_ms)],
      ['Enforcement', 'p99 Latency (ms)', cell(metrics.enforcement.p99_latency_ms)],
      ['Enforcement', 'Latency Sample Size', cell(metrics.enforcement.latency_sample_size)],
      ['Threat Categories', 'Prompt Injection', cell(metrics.threat_defense.prompt_injection)],
      ['Threat Categories', 'Sensitive Data Egress', cell(metrics.threat_defense.sensitive_data_egress)],
      ['Threat Categories', 'Prohibited Administrative Action', cell(metrics.threat_defense.prohibited_admin_action)],
      ['Threat Categories', 'Outside Capability Whitelist', cell(metrics.threat_defense.outside_capability_whitelist)],
      ['Threat Categories', 'Transaction Limit Exceeded', cell(metrics.threat_defense.transaction_limit_exceeded)],
      ['Threat Categories', 'Quarantined Agent Attempt', cell(metrics.threat_defense.quarantined_agent_attempt)],
      ['Fleet', 'Total Active Agents', cell(metrics.fleet.total_agents)],
      ['Fleet', 'Quarantined Agents', cell(metrics.fleet.quarantined)],
      ['Fleet', 'Average Risk Score', cell(metrics.fleet.avg_risk_score)],
      ['Incidents', 'Total', cell(metrics.incidents.total)],
      ['Incidents', 'MTTR (minutes)', cell(metrics.incidents.mttr_minutes)],
      ['Incidents', 'MTTR Sample Size', cell(metrics.incidents.mttr_sample_size)]
    ];
    const csvContent =
      'data:text/csv;charset=utf-8,' + rows.map((e) => e.map((v) => `"${v.replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = encodeURI(csvContent);
    a.download = `agenticscale-metrics-summary-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-24 text-sm text-slate-500 dark:text-slate-400">
        <RefreshCw className="h-4 w-4 animate-spin" />
        <span>Reading metrics from D1&hellip;</span>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-200">
        <p className="font-bold">Metrics are unavailable.</p>
        <p className="mt-1 text-xs">{error || 'The metrics endpoint did not return data.'}</p>
        <button
          type="button"
          onClick={fetchMetrics}
          className="mt-3 rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-bold hover:bg-rose-100 dark:border-rose-800 dark:hover:bg-rose-950/40"
        >
          Try again
        </button>
      </div>
    );
  }

  const { evidence_basis: basis, enforcement, threat_defense: threats, telemetry, incidents, fleet, infrastructure } = metrics;
  const presentation = MODE_PRESENTATION[basis.mode] ?? MODE_PRESENTATION.empty;
  const hasEvents = enforcement.total_evaluated_actions > 0;
  const interception =
    enforcement.block_percentage === null || enforcement.review_percentage === null
      ? null
      : enforcement.block_percentage + enforcement.review_percentage;

  const toneClasses =
    presentation.tone === 'emerald'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-500/30'
      : presentation.tone === 'amber'
        ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-500/30'
        : 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600';

  return (
    <div className="space-y-6 pb-12 animate-fade-in">

      {/* Header & Controls */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 border-b border-slate-200 pb-5 dark:border-slate-800">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">
              Platform Metrics
            </h1>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${toneClasses}`}>
              <Radio className={`h-2.5 w-2.5 ${basis.mode === 'live' ? 'animate-pulse' : ''}`} />
              <span>{presentation.label}</span>
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-xs sm:text-sm text-slate-600 dark:text-slate-400">
            Every figure on this page is counted from this workspace's own rows in Cloudflare D1. Metrics with no
            underlying record show <span className="font-bold">&mdash;</span> rather than an estimate.
          </p>

          {basis.notice && (
            <div className="mt-3 flex max-w-3xl items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-200">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{basis.notice}</span>
            </div>
          )}

          {basis.measurement_window?.first_event_at && (
            <p className="mt-2 font-mono text-[11px] text-slate-500 dark:text-slate-400">
              Measured over {new Date(basis.measurement_window.first_event_at).toLocaleDateString()} &rarr;{' '}
              {basis.measurement_window.last_event_at
                ? new Date(basis.measurement_window.last_event_at).toLocaleDateString()
                : 'now'}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={fetchMetrics}
            disabled={isRefreshing}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-xs transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            title="Re-read metrics from D1"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin text-brand-600' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          <button
            type="button"
            onClick={handleExportCsv}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-3 py-2 text-xs font-bold text-white shadow-xs transition hover:bg-brand-500"
          >
            <Download className="h-3.5 w-3.5" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      <AdoptionPanel />

      {/* Headline Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs dark:border-slate-800 dark:bg-slate-900 transition-all hover:shadow-md">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span className="text-xs font-bold uppercase tracking-wider">Evaluated Actions</span>
            <div className="rounded-xl bg-brand-50 p-2 dark:bg-brand-500/15">
              <Activity className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            </div>
          </div>
          <div className="mt-3 text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            {enforcement.total_evaluated_actions.toLocaleString()}
          </div>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Safety events recorded for this workspace
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs dark:border-slate-800 dark:bg-slate-900 transition-all hover:shadow-md">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span className="text-xs font-bold uppercase tracking-wider">Decision Latency (p50)</span>
            <div className="rounded-xl bg-amber-50 p-2 dark:bg-amber-500/15">
              <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <div className="mt-3 text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            <Measured value={enforcement.p50_latency_ms} suffix="ms" absent="No safety events recorded yet" />
          </div>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            p95 <Measured value={enforcement.p95_latency_ms} /> &middot; p99{' '}
            <Measured value={enforcement.p99_latency_ms} /> &middot; n={enforcement.latency_sample_size}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs dark:border-slate-800 dark:bg-slate-900 transition-all hover:shadow-md">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span className="text-xs font-bold uppercase tracking-wider">Interception Rate</span>
            <div className="rounded-xl bg-rose-50 p-2 dark:bg-rose-500/15">
              <ShieldCheck className="h-4 w-4 text-rose-600 dark:text-rose-400" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
              {interception === null ? <Measured value={null} absent="No safety events recorded yet" /> : `${interception}%`}
            </span>
            {hasEvents && (
              <span className="text-xs font-semibold text-slate-500">
                ({(enforcement.blocked + enforcement.held_for_review).toLocaleString()} actions)
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            <Measured value={enforcement.block_percentage} />% blocked &middot;{' '}
            <Measured value={enforcement.review_percentage} />% held for review
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs dark:border-slate-800 dark:bg-slate-900 transition-all hover:shadow-md">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span className="text-xs font-bold uppercase tracking-wider">Connected Applications</span>
            <div className="rounded-xl bg-emerald-50 p-2 dark:bg-emerald-500/15">
              <Globe2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <div className="mt-3 text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            {telemetry.connected_applications.toLocaleString()}
          </div>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            {telemetry.active_integration_keys.toLocaleString()} active integration key
            {telemetry.active_integration_keys === 1 ? '' : 's'}
          </p>
        </div>

      </div>

      {/* Threat categories */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-base font-extrabold text-slate-900 dark:text-white">
              <Flame className="h-5 w-5 text-rose-500" />
              <span>Recorded Threat Categories</span>
            </h2>
            <p className="mt-0.5 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
              {threats.derivation}
            </p>
          </div>
          <span className="shrink-0 font-mono text-xs text-slate-400">
            Synced {lastRefreshed.toLocaleTimeString()}
          </span>
        </div>

        {!hasEvents ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center dark:border-slate-700 dark:bg-slate-950/40">
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300">No safety events recorded yet</p>
            <p className="mx-auto mt-1 max-w-md text-xs text-slate-500 dark:text-slate-400">
              Threat category counts appear once this workspace evaluates its first agent action through the gateway.
              Until then there is nothing to count, so nothing is shown.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Prompt Injection / Jailbreak', tag: 'LLM01', value: threats.prompt_injection },
              { label: 'Sensitive Data Egress', tag: 'LLM02', value: threats.sensitive_data_egress },
              { label: 'Prohibited Administrative Action', tag: 'LLM06', value: threats.prohibited_admin_action },
              { label: 'Outside Capability Whitelist', tag: 'LLM06', value: threats.outside_capability_whitelist },
              { label: 'Transaction Limit Exceeded', tag: 'HITL Gate', value: threats.transaction_limit_exceeded },
              { label: 'Quarantined Agent Attempt', tag: 'Circuit Breaker', value: threats.quarantined_agent_attempt }
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-950/40"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{item.label}</span>
                  <span className="shrink-0 rounded-md bg-slate-200 px-2 py-0.5 font-mono text-xs font-extrabold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    {item.tag}
                  </span>
                </div>
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-2xl font-black text-slate-900 dark:text-white">
                    {item.value.toLocaleString()}
                  </span>
                  <span className="text-[11px] text-slate-400">of {threats.sample_size.toLocaleString()} events</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enforcement distribution & infrastructure bindings */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs dark:border-slate-800 dark:bg-slate-900">
          <h2 className="flex items-center gap-2 text-base font-extrabold text-slate-900 dark:text-white">
            <Layers className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <span>Enforcement Decision Distribution</span>
          </h2>
          <p className="mt-0.5 mb-5 text-xs text-slate-500 dark:text-slate-400">
            Every recorded decision across this workspace's deterministic safety policies.
          </p>

          {!hasEvents ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center dark:border-slate-700 dark:bg-slate-950/40">
              <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Nothing evaluated yet</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Connect an application and evaluate an action to populate this breakdown.
              </p>
            </div>
          ) : (
            <>
              <div className="flex h-4 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  style={{ width: `${enforcement.allow_percentage ?? 0}%` }}
                  className="bg-emerald-500 transition-all duration-500"
                  title={`Allowed: ${enforcement.allow_percentage}%`}
                />
                <div
                  style={{ width: `${enforcement.review_percentage ?? 0}%` }}
                  className="bg-amber-500 transition-all duration-500"
                  title={`Review gate: ${enforcement.review_percentage}%`}
                />
                <div
                  style={{ width: `${enforcement.block_percentage ?? 0}%` }}
                  className="bg-rose-500 transition-all duration-500"
                  title={`Blocked: ${enforcement.block_percentage}%`}
                />
              </div>

              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 dark:border-emerald-500/20 dark:bg-emerald-950/20">
                  <span className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="h-3 w-3" /> Allowed
                  </span>
                  <p className="mt-1 text-lg font-black text-emerald-800 dark:text-emerald-200">
                    {enforcement.allowed.toLocaleString()}
                  </p>
                  <span className="font-mono text-[10px] text-slate-500">{enforcement.allow_percentage}%</span>
                </div>

                <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 dark:border-amber-500/20 dark:bg-amber-950/20">
                  <span className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="h-3 w-3" /> Review Gate
                  </span>
                  <p className="mt-1 text-lg font-black text-amber-800 dark:text-amber-200">
                    {enforcement.held_for_review.toLocaleString()}
                  </p>
                  <span className="font-mono text-[10px] text-slate-500">{enforcement.review_percentage}%</span>
                </div>

                <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-3 dark:border-rose-500/20 dark:bg-rose-950/20">
                  <span className="flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300">
                    <XCircle className="h-3 w-3" /> Blocked
                  </span>
                  <p className="mt-1 text-lg font-black text-rose-800 dark:text-rose-200">
                    {enforcement.blocked.toLocaleString()}
                  </p>
                  <span className="font-mono text-[10px] text-slate-500">{enforcement.block_percentage}%</span>
                </div>
              </div>
            </>
          )}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 text-xs dark:border-slate-800">
            <span className="text-slate-500 dark:text-slate-400">Authorized fleet spending cap</span>
            <strong className="font-mono font-bold text-slate-900 dark:text-white">
              ${fleet.total_spending_limit.toLocaleString()} USD
            </strong>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="text-slate-500 dark:text-slate-400">
              Mean time to resolve <span className="font-mono">(n={incidents.mttr_sample_size})</span>
            </span>
            <strong className="font-mono font-bold text-slate-900 dark:text-white">
              <Measured value={incidents.mttr_minutes} suffix="min" absent="No incident has been resolved yet" />
            </strong>
          </div>
        </div>

        {/* Infrastructure bindings — observed, not asserted */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs dark:border-slate-800 dark:bg-slate-900">
          <h2 className="flex items-center gap-2 text-base font-extrabold text-slate-900 dark:text-white">
            <Server className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <span>Runtime Bindings</span>
          </h2>
          <p className="mt-0.5 mb-4 text-xs text-slate-500 dark:text-slate-400">
            What this Worker can actually observe about its own environment. An unbound resource means the
            corresponding feature is inactive in this deployment.
          </p>

          <div className="space-y-2.5">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-xs dark:border-slate-800 dark:bg-slate-800/60">
              <div className="flex items-center gap-2.5">
                <Database className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <div>
                  <span className="font-bold text-slate-900 dark:text-white">Cloudflare D1</span>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Safety events, incidents, and audit log</p>
                </div>
              </div>
              <BindingBadge state={infrastructure.d1_binding} />
            </div>

            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-xs dark:border-slate-800 dark:bg-slate-800/60">
              <div className="flex items-center gap-2.5">
                <Zap className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
                <div>
                  <span className="font-bold text-slate-900 dark:text-white">Cloudflare KV</span>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">{infrastructure.rate_limiter_mode}</p>
                </div>
              </div>
              <BindingBadge state={infrastructure.kv_binding} />
            </div>

            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-xs dark:border-slate-800 dark:bg-slate-800/60">
              <div className="flex items-center gap-2.5">
                <HardDrive className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <div>
                  <span className="font-bold text-slate-900 dark:text-white">R2 Evidence Archive</span>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    {infrastructure.r2_archive_binding === 'BOUND'
                      ? 'Long-term evidence retention'
                      : 'Not configured — evidence lives in D1 only'}
                  </p>
                </div>
              </div>
              <BindingBadge state={infrastructure.r2_archive_binding} />
            </div>

            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-xs dark:border-slate-800 dark:bg-slate-800/60">
              <div className="flex items-center gap-2.5">
                <Cpu className="h-4 w-4 shrink-0 text-purple-600 dark:text-purple-400" />
                <div>
                  <span className="font-bold text-slate-900 dark:text-white">Workers AI Classifier</span>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    {infrastructure.workers_ai_binding === 'BOUND'
                      ? 'Supplements the deterministic engine'
                      : 'Not bound — all decisions are deterministic'}
                  </p>
                </div>
              </div>
              <BindingBadge state={infrastructure.workers_ai_binding} />
            </div>

            {infrastructure.colo && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-xs dark:border-slate-800 dark:bg-slate-800/60">
                <div className="flex items-center gap-2.5">
                  <Globe2 className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
                  <div>
                    <span className="font-bold text-slate-900 dark:text-white">Serving Location</span>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Colo that handled this request</p>
                  </div>
                </div>
                <span className="rounded-md bg-slate-200 px-2 py-0.5 font-mono text-[10px] font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {infrastructure.colo}
                </span>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Footer summary */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-5 text-xs dark:border-slate-800 dark:bg-slate-900/60">
        <div className="flex flex-wrap items-center gap-4 text-slate-600 dark:text-slate-300">
          <span><strong>{telemetry.total_spans_logged.toLocaleString()}</strong> spans recorded</span>
          <span><strong>{telemetry.total_traces_recorded.toLocaleString()}</strong> traces indexed</span>
          <span><strong>{telemetry.total_agent_runs.toLocaleString()}</strong> agent runs</span>
          <span><strong>{telemetry.active_custom_guardrails.toLocaleString()}</strong> active policies</span>
          <span>
            Workspace:{' '}
            <code className="font-mono font-bold text-slate-900 dark:text-white">{metrics.organization_id}</code>
          </span>
        </div>

        <button
          type="button"
          onClick={handleExportJson}
          className="font-bold text-brand-600 hover:underline dark:text-brand-400"
        >
          Download raw JSON &rarr;
        </button>
      </div>

    </div>
  );
};
